// imports
import dayjs from 'dayjs';
import utcPlugin from 'dayjs/plugin/utc';
import durationPlugin from 'dayjs/plugin/duration';
import { HeroNFT } from '../repositories/web3/types';
import {
  Quest, QuestStatus, TimeFormat, ITraitMatching
} from './types';

// interfaces / types / consts
dayjs.extend(utcPlugin);// enable UTC mode
dayjs.extend(durationPlugin);// enable durations

// ops - dom
const isMap = (): boolean => window.location.pathname === '/play';
const isBackpack = (): boolean => window.location.pathname === '/backpack';
const getCookie = (name: string): unknown | undefined => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) { return parts?.pop()?.split(';').shift(); }
  return undefined;
};

// ops - hero
const getRarity = (heroTraits: string[] = []): string => {
  const rarityTrait = heroTraits.find((trait) => trait.includes('rarity_'))?.split('_')[1];
  return rarityTrait || '';
};
const getSpecies = (heroTraits: string[] = []): string => {
  const speciesTrait = heroTraits.find((trait) => trait.includes('species_'))?.split('_')[1];
  return speciesTrait || '';
};

// ops - quest
const getStatus = (quest: Quest): QuestStatus => {
  if (!quest.startTime || !quest.endTime) { return QuestStatus.available; }
  const currentTime = dayjs();
  return currentTime.isBefore(dayjs(quest.endTime * 1000)) && currentTime.isAfter(dayjs(quest.startTime * 1000))
    ? QuestStatus.inProgress : QuestStatus.completed;
};
const getTraitMatching = (heroTraits: string[] = [], questTraits: string[] = []): { heroes: ITraitMatching[]; quests: ITraitMatching[] } => {
  const heroMatchedIdxs: number[] = [];
  const questTraitMatching: ITraitMatching[] = [];
  questTraits.forEach((questTrait) => {
    const isClassTrait = questTrait.includes('class_');
    const isRankTrait = questTrait.includes('rank_');
    const isSpecific = (isClassTrait && isRankTrait);
    const heroTraitIdx = heroTraits.findIndex((heroTrait) => (
      ((isRankTrait || isClassTrait) && !isSpecific) ? heroTrait.includes(questTrait) : heroTrait === questTrait
    ));
    if (heroTraitIdx !== -1) { heroMatchedIdxs.push(heroTraitIdx); }
    questTraitMatching.push({ id: questTrait, icon: questTrait.toUpperCase(), status: heroTraitIdx !== -1 ? 'success' : 'error' });
  });
  const heroTraitMatching: ITraitMatching[] = heroTraits.map((trait, idx) => (
    { id: trait, icon: trait.toUpperCase(), status: heroMatchedIdxs.includes(idx) ? 'success' : 'error' }
  ));
  return {
    heroes: heroTraitMatching.sort((a, b) => {
      if (a.status < b.status) return 1;
      if (a.status > b.status) return -1;
      return 0;
    }),
    quests: questTraitMatching
  };
};
const getQuestTraitMatching = (questTraits: string[][], crew: number[], heroNFTs: HeroNFT[]): ITraitMatching[][][] => {
  const crewTM: ITraitMatching[][] = [];
  const questTM: ITraitMatching[][] = [];
  questTraits.forEach((slotTraits, idx) => {
    const member = crew[idx] ? heroNFTs.find((hero) => hero.tokenId === crew[idx]) : undefined;
    if (member) {
      const { quests, heroes } = getTraitMatching(member.traitIds, slotTraits);
      crewTM[idx] = heroes;
      questTM[idx] = quests;
    } else {
      crewTM[idx] = [];
      questTM[idx] = slotTraits.map((trait) => ({ id: trait, icon: trait.toUpperCase(), status: 'idle' }));
    }
  });
  return [crewTM, questTM];
};
const getRewardChances = (quest: Quest, questTraitMatching: ITraitMatching[][]): number[] => {
  let rewardPoints = 0;
  questTraitMatching.forEach((slotTraitMatching, slotIdx) => {
    const isHeroSelected = Boolean(slotTraitMatching.filter((traitMatching) => traitMatching.status !== 'idle').length);
    if (isHeroSelected) {
      rewardPoints += quest.points.heroes[slotIdx];
      slotTraitMatching.forEach((slotMatching) => {
        if (slotMatching.status === 'success') {
          const questTraitIdx = quest.traits[slotIdx].findIndex((questTrait) => questTrait === slotMatching.id);
          rewardPoints += quest.points.traits[slotIdx][questTraitIdx];
        }
      });
    }
  });
  const rewardChances: number[] = [];
  const BASE_DIVDER = 10000;
  quest.rewards.forEach((reward) => {
    const {
      minChance, maxChance, minThreshold, maxThreshold
    } = reward;
    if (rewardPoints < minThreshold) {
      rewardChances.push(minChance);
      return;
    }
    let chancePercentage = Math.trunc((((rewardPoints - minThreshold) * BASE_DIVDER) * 100) / (maxThreshold - minThreshold));
    if (chancePercentage > (BASE_DIVDER * 100)) { chancePercentage = BASE_DIVDER * 100; }
    rewardChances.push(
      Math.trunc(((minChance * BASE_DIVDER) + (((chancePercentage / 100) * (maxChance - minChance)) + (BASE_DIVDER / 2))) / BASE_DIVDER)
    );
  });
  return rewardChances;
};

// ops - date
const getYear = (): number => dayjs().year();
const getRemainingMS = (timeS: number): number => dayjs(timeS * 1000).diff(dayjs().valueOf());
const getQuestProgress = (startTime: number, endTime: number): number => {
  const start = dayjs(startTime * 1000);
  const totalTime = dayjs(endTime * 1000).diff(start, 'millisecond');
  const elapsedTime = dayjs().diff(start, 'millisecond');
  return Math.round((elapsedTime / totalTime) * 100);
};
const formatTime = (timeInMS: number): TimeFormat => {
  const duration = dayjs.duration(timeInMS);
  const days = duration.days();
  const hours = duration.hours();
  const minutes = duration.minutes();
  const seconds = duration.seconds();
  if (days > 0) {
    return hours > 0 ? `${days}d ${hours}h` : `${days}d`;
  } if (hours > 0) {
    return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
  } if (minutes > 0) {
    return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`;
  }
  return `${seconds}s`;
};
const formatRemainingTime = (endTimeS: number): TimeFormat => {
  const diffInMs = getRemainingMS(endTimeS);
  return (diffInMs < 0) ? '0s' : formatTime(diffInMs);
};

// ops - string
const formatNumber = (value: number): string => {
  if (value < 1000) { return `${value}`; }
  if (value < 10000) {
    const valueStr = `${value}`;
    return `${valueStr.slice(0, 1)},${valueStr.slice(1)}`;
  }
  let number = value;
  const suffixes = ['', 'K', 'M', 'B'];
  let suffixIndex = 0;
  while (number >= 1000 && suffixIndex < suffixes.length - 1) {
    number /= 1000;
    suffixIndex += 1;
  }
  const formattedNumber = number.toFixed(2).replace(/\.?0+$/, '');
  const formattedWithSuffix = formattedNumber + suffixes[suffixIndex];
  return formattedWithSuffix;
};
const parseDataURI = (dataURI: string): object => {
  if (!dataURI) { return {}; }
  const tokens = dataURI.split('data:application/json;base64,');
  const json = atob(tokens[1] || 'e30=');// `e30 = ` == btoa('{}')
  return JSON.parse(json);
};
const kebabCase = (str: string): string | undefined => {
  const regex = /[A-Z]{2,}(?=[A-Z][a-z]+[0-9]*|\b)|[A-Z]?[a-z]+[0-9]*|[A-Z]|[0-9]+/g;
  return str && str.match(regex)?.map((token) => token.toLowerCase()).join('-');
};
const validateEmail = (email: string | undefined): boolean => (email ? /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) : false);
const snakeCase = (str: string): string => str.replaceAll(' ', '_').replaceAll('-', '_').replaceAll("'", '').toLowerCase();

// exports
export const DOM_UTILS = { isMap, isBackpack, getCookie };
export const HERO_UTILS = { getRarity, getSpecies };
export const DATE_UTILS = {
  getYear, formatTime, formatRemainingTime, getRemainingMS
};
export const QUEST_UTILS = {
  getStatus,
  getRewardChances,
  getQuestProgress,
  getTraitMatching,
  getQuestTraitMatching
};
export const STRING_UTILS = {
  kebabCase, snakeCase, validateEmail, parseDataURI, formatNumber
};
const Utils = {
  dom: DOM_UTILS,
  hero: HERO_UTILS,
  date: DATE_UTILS,
  quest: QUEST_UTILS,
  string: STRING_UTILS
};
export default Utils;
